/* FlightSimBuilder — beta feedback form.
   Posts JSON to the build-tracker app, which emails beta@flightsimbuilder.com.
   Screenshots are downscaled in the browser: Vercel caps a serverless request body
   at ~4.5 MB and base64 adds ~33%, so raw phone/4K screenshots would not fit. */
(function () {
  'use strict';

  var MAX_FILES = 3;
  var PASSTHROUGH_BYTES = 700 * 1024; // already small — keep the original, crisper for text
  var TARGET_BYTES = 1500 * 1024; // per image after processing
  var HARD_BYTES = 2 * 1024 * 1024; // endpoint rejects anything above this
  var MAX_EDGE = 2400; // longest side after downscaling
  var TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  function prettySize(bytes) {
    return bytes < 1024 * 1024
      ? Math.max(1, Math.round(bytes / 1024)) + ' KB'
      : (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  function readAsDataURL(file) {
    return new Promise(function (resolve, reject) {
      var fr = new FileReader();
      fr.onload = function () { resolve(String(fr.result)); };
      fr.onerror = function () { reject(new Error('read failed')); };
      fr.readAsDataURL(file);
    });
  }

  function loadImage(dataUrl) {
    return new Promise(function (resolve, reject) {
      var img = new Image();
      img.onload = function () { resolve(img); };
      img.onerror = function () { reject(new Error('decode failed')); };
      img.src = dataUrl;
    });
  }

  /** Returns { filename, dataBase64, bytes } or throws with a human-readable message. */
  function processFile(file) {
    if (TYPES.indexOf(file.type) === -1) {
      return Promise.reject(new Error(file.name + ' is not an image (PNG, JPG, WebP or GIF).'));
    }

    // Animated GIFs can't survive a canvas round trip, so they pass through or not at all.
    var mustPassThrough = file.type === 'image/gif';

    if (file.size <= PASSTHROUGH_BYTES || mustPassThrough) {
      if (file.size > HARD_BYTES) {
        return Promise.reject(new Error(file.name + ' is too large (' + prettySize(file.size) + ', max 2 MB).'));
      }
      return readAsDataURL(file).then(function (url) {
        return { filename: file.name, dataBase64: url.split(',')[1] || '', bytes: file.size };
      });
    }

    return readAsDataURL(file)
      .then(loadImage)
      .then(function (img) {
        var scale = Math.min(1, MAX_EDGE / Math.max(img.width, img.height));
        var w = Math.max(1, Math.round(img.width * scale));
        var h = Math.max(1, Math.round(img.height * scale));
        var canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        var ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff'; // JPEG has no alpha; avoid black boxes behind transparency
        ctx.fillRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);

        var out = canvas.toDataURL('image/jpeg', 0.9);
        if (out.length * 0.75 > TARGET_BYTES) out = canvas.toDataURL('image/jpeg', 0.72);

        var b64 = out.split(',')[1] || '';
        var bytes = Math.round(b64.length * 0.75);
        if (bytes > HARD_BYTES) {
          throw new Error(file.name + ' is too large to send even after compressing. Try cropping it.');
        }
        return { filename: file.name.replace(/\.[^.]*$/, '') + '.jpg', dataBase64: b64, bytes: bytes };
      })
      .catch(function (err) {
        // Canvas failed (tainted/decoder issue) — fall back to the original if it fits.
        if (err && /too large/.test(err.message)) throw err;
        if (file.size <= HARD_BYTES) {
          return readAsDataURL(file).then(function (url) {
            return { filename: file.name, dataBase64: url.split(',')[1] || '', bytes: file.size };
          });
        }
        throw new Error('Could not process ' + file.name + '. Try a smaller screenshot.');
      });
  }

  ready(function () {
    var form = document.querySelector('[data-fsbq-beta]');
    if (!form) return;

    var endpoint = form.getAttribute('data-endpoint');
    var errorBox = form.querySelector('[data-beta-error]');
    var card = form.parentNode;
    var success = card.querySelector('[data-beta-success]');
    var againBtn = card.querySelector('[data-beta-again]');
    var submit = form.querySelector('.fsbq-submit');
    var submitLabel = submit ? submit.textContent : 'Send feedback';

    var fileInput = form.querySelector('[data-beta-files]');
    var fileList = form.querySelector('[data-beta-filelist]');
    var attachments = [];

    function showError(msg) {
      if (!errorBox) return;
      errorBox.textContent = msg;
      errorBox.hidden = false;
      errorBox.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }

    function clearError() {
      if (errorBox) errorBox.hidden = true;
    }

    function setBusy(busy) {
      if (!submit) return;
      submit.disabled = busy;
      submit.textContent = busy ? 'Sending…' : submitLabel;
    }

    function renderFiles() {
      if (!fileList) return;
      fileList.innerHTML = '';
      attachments.forEach(function (a, i) {
        var li = document.createElement('li');
        li.className = 'fsbq-file';
        var name = document.createElement('span');
        name.className = 'fsbq-file__name';
        name.textContent = a.filename;
        var size = document.createElement('span');
        size.className = 'fsbq-file__size';
        size.textContent = prettySize(a.bytes);
        var rm = document.createElement('button');
        rm.type = 'button';
        rm.className = 'fsbq-file__remove';
        rm.setAttribute('aria-label', 'Remove ' + a.filename);
        rm.textContent = '×';
        rm.addEventListener('click', function () {
          attachments.splice(i, 1);
          renderFiles();
        });
        li.appendChild(name);
        li.appendChild(size);
        li.appendChild(rm);
        fileList.appendChild(li);
      });
      fileList.hidden = attachments.length === 0;
    }

    if (fileInput) {
      fileInput.addEventListener('change', function () {
        var chosen = Array.prototype.slice.call(fileInput.files || []);
        fileInput.value = ''; // let the same file be re-picked after removing it
        if (!chosen.length) return;
        clearError();

        var room = MAX_FILES - attachments.length;
        if (room <= 0) {
          showError('You can attach up to ' + MAX_FILES + ' screenshots.');
          return;
        }
        if (chosen.length > room) {
          showError('Only the first ' + room + ' of those were added (max ' + MAX_FILES + ').');
          chosen = chosen.slice(0, room);
        }

        fileInput.disabled = true;
        Promise.all(
          chosen.map(function (f) {
            return processFile(f).then(
              function (a) { return { ok: true, a: a }; },
              function (e) { return { ok: false, msg: e.message }; }
            );
          })
        ).then(function (results) {
          fileInput.disabled = false;
          var failures = [];
          results.forEach(function (r) {
            if (r.ok) attachments.push(r.a);
            else failures.push(r.msg);
          });
          renderFiles();
          if (failures.length) showError(failures.join(' '));
        });
      });
    }

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      clearError();

      // novalidate is on the form so we can place the message ourselves,
      // but the native constraints are still worth running first.
      if (!form.checkValidity()) {
        form.reportValidity();
        return;
      }

      var data = {
        name: form.elements.name.value,
        email: form.elements.email.value,
        simulator: form.elements.simulator.value,
        relatedTo: form.elements.relatedTo.value,
        buildVersion: form.elements.buildVersion ? form.elements.buildVersion.value : '',
        location: form.elements.location ? form.elements.location.value : '',
        message: form.elements.message.value,
        company: form.elements.company ? form.elements.company.value : '',
        pageUrl: window.location.href,
        attachments: attachments.map(function (a) {
          return { filename: a.filename, dataBase64: a.dataBase64 };
        })
      };

      setBusy(true);

      fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      })
        .then(function (res) {
          return res.json().catch(function () { return {}; }).then(function (body) {
            return { ok: res.ok, body: body };
          });
        })
        .then(function (r) {
          setBusy(false);
          if (r.ok && r.body.ok) {
            form.style.display = 'none';
            if (success) {
              success.classList.add('is-active');
              success.setAttribute('tabindex', '-1');
              success.focus();
            }
            form.reset();
            attachments = [];
            renderFiles();
            return;
          }
          showError(r.body.error || 'Something went wrong sending that. Please try again.');
        })
        .catch(function () {
          setBusy(false);
          showError('We could not reach the feedback service. Check your connection and try again.');
        });
    });

    if (againBtn) {
      againBtn.addEventListener('click', function () {
        if (success) success.classList.remove('is-active');
        form.style.display = '';
        clearError();
        form.elements.name.focus();
      });
    }
  });
})();
